import { PropertyFilterProps } from "@amzn/awsui-components-react";
import { convertTokensToAndFilters, convertTokensToPropertyFilter } from "common/utils/property-filter-helper";
import { LocationGroup, OrderFilteringOptions, QueryObject, SEARCH_ORDERS_PROPERTY_KEYS, SortingState } from "./useOrderFiltering";
import { buildingIdFormat } from "interfaces";
import { CheckboxNode } from "common/component/FilterSection/CheckboxFilter/CheckboxFilter";

/**
 * Extracts primary selected locations from the hierarchical checkbox structure.
 * Traverses the tree to find all selected location nodes.
 *
 * @param locationSelectedItems - Array of checkbox nodes representing location hierarchy
 * @returns Array of selected location objects with building ID format
 */
export const getPrimarySelectedLocations = (locationSelectedItems: CheckboxNode[]): buildingIdFormat[] => {
    const primaryLocations: buildingIdFormat[] = [];
    const queue = [...locationSelectedItems];

    while (queue.length > 0) {
        const location = queue.shift()!;

        if (location.selected) {
            primaryLocations.push(JSON.parse(decodeURIComponent(location.id)) as buildingIdFormat);
        } else if (location.children) {
            queue.push(...Array.from(location.children));
        }
    }

    return primaryLocations;
};

/**
 * Groups selected locations by their type (region, country, city, building).
 * This enables separate filtering by location hierarchy level.
 *
 * @param selectedLocations - Array of selected location objects
 * @returns Object with location names grouped by type
 */
export const groupLocationsByType = (selectedLocations: buildingIdFormat[]): LocationGroup => {
    return selectedLocations.reduce((acc, location) => {
        acc[location.type] = acc[location.type] || [];
        acc[location.type].push(location.name);
        return acc;
    }, {} as LocationGroup);
};

/**
 * Creates MongoDB-style OR filters for location-based queries.
 * Each location type becomes a separate filter condition.
 *
 * @param locationGroups - Location names grouped by type
 * @returns Array of filter objects for MongoDB OR operations
 */
export const createLocationFilters = (locationGroups: LocationGroup): Array<{ [key: string]: string[] }> => {
    const orFilters: Array<{ [key: string]: string[] }> = [];

    if (locationGroups.region?.length > 0) {
        orFilters.push({ "details.deliverTo.region": locationGroups.region });
    }
    if (locationGroups.country?.length > 0) {
        orFilters.push({ "details.deliverTo.country": locationGroups.country });
    }
    if (locationGroups.city?.length > 0) {
        orFilters.push({ "details.deliverTo.city": locationGroups.city });
    }
    if (locationGroups.building?.length > 0) {
        orFilters.push({ "details.deliverTo.building": locationGroups.building });
    }

    return orFilters;
};

/**
 * Extracts keyword tokens and creates search filters across all searchable fields.
 * Keywords without property keys are treated as global search terms.
 *
 * @param tokens - Property filter tokens from the search interface
 * @returns Object containing keyword filters and extracted keywords
 */
export const createKeywordFilters = (tokens: ReadonlyArray<PropertyFilterProps.Token>) => {
    const keywords = tokens
        .filter((token) => !token.propertyKey && token.value.trim())
        .map((token) => token.value.trim());

    const keywordFilters = keywords.map((keyword) => ({
        $or: Object.values(SEARCH_ORDERS_PROPERTY_KEYS).map((key) => ({ [key]: keyword })),
    }));

    return { keywordFilters, keywords };
};

/**
 * Builds a comprehensive MongoDB-style query object from all filter options.
 * Combines property filters, keywords, location filters, and sorting parameters.
 *
 * @param filterOptions - All current filter selections and configurations
 * @param selectedLocations - Memoized selected locations to avoid recalculation
 * @param [currentSorting] - Current sorting state for the table
 * @returns Complete query object for the orders API
 */
export const buildQueryObject = (
    filterOptions: ReturnType<typeof OrderFilteringOptions>,
    selectedLocations: buildingIdFormat[],
    currentSorting?: SortingState,
): QueryObject => {
    const queryObject: QueryObject = {};

    // Requester filter
    const requesters = filterOptions.requesterQuery.tokens
        .filter((token) => token.value?.length > 0)
        .map((token) => token.value.trim().toLowerCase());

    if (requesters.length > 0) {
        queryObject["requested.by"] = requesters;
    }

    // Status filter
    const selectedStatuses = filterOptions.orderStatus.filter((status) => status.selected).map((status) => status.id);

    if (selectedStatuses.length > 0) {
        queryObject.status = selectedStatuses;
    }

    // Sorting
    if (currentSorting?.sortOrder && currentSorting?.sortBy) {
        queryObject[currentSorting.sortOrder] = [currentSorting.sortBy];
    }

    // Handle property filters and keywords
    const { keywordFilters } = createKeywordFilters(filterOptions.query.tokens);
    const formattedQuery = convertTokensToPropertyFilter(filterOptions.query.tokens);

    // Location filters
    const locationGroups = groupLocationsByType(selectedLocations);
    const locationOrFilters = createLocationFilters(locationGroups);

    // Build query based on operation type
    if (filterOptions.query.operation === "and") {
        const andFilterObjects = convertTokensToAndFilters(filterOptions.query.tokens);
        const allAndFilters = [...andFilterObjects, ...keywordFilters];

        if (allAndFilters.length > 0) {
            queryObject.$and = allAndFilters;
        }

        if (locationOrFilters.length > 0) {
            queryObject.$or = locationOrFilters;
        }
    } else if (filterOptions.query.operation === "or") {
        const orderFilters = Object.keys(formattedQuery)
            .filter((key) => formattedQuery[key]?.length > 0)
            .map((key) => ({ [key]: formattedQuery[key] }));

        const andFilters = [];

        if (orderFilters.length > 0 || keywordFilters.length > 0) {
            andFilters.push({ $or: [...orderFilters, ...keywordFilters] });
        }

        if (locationOrFilters.length > 0) {
            andFilters.push({ $or: locationOrFilters });
        }

        if (andFilters.length > 0) {
            queryObject.$and = andFilters;
        }
    } else if (locationOrFilters.length > 0) {
        queryObject.$or = locationOrFilters;
    }

    return queryObject;
};

/**
 * Normalizes a property filter query for consistent comparison.
 * Sorts tokens by property key, value, and operator to ensure
 * queries with identical content but different token order are
 * considered equal.
 *
 * @param q - The property filter query to normalize
 * @returns Normalized query with sorted tokens
 */
export const normalizeQuery = (q: PropertyFilterProps.Query): PropertyFilterProps.Query => ({
    ...q,
    tokens: [...q.tokens].sort((a, b) => {
        const keyA = a.propertyKey ?? "";
        const keyB = b.propertyKey ?? "";
        const keyCompare = keyA.localeCompare(keyB);
        if (keyCompare !== 0) return keyCompare;

        const valueCompare = String(a.value).localeCompare(String(b.value));
        if (valueCompare !== 0) return valueCompare;

        return (a.operator || "").localeCompare(b.operator || "");
    }),
});
